import type {TBBox} from '@postermywall/fabricjs-2';
import {Point} from '@postermywall/fabricjs-2';
import type {Page} from '@PosterWhiteboard/page/page.class';
import type {RGBA} from '@Utils/color.util';
import {getPerceivedBrightness} from '@Utils/color.util';

const BLACK_FILL: RGBA = [0, 0, 0, 1];
const WHITE_FILL: RGBA = [255, 255, 255, 1];

/**
 * Average backdrop brightness (0..1) at or above which black is the readable pick; below it, white.
 * The mathematical black↔white contrast crossover sits at ≈0.46; 0.5 rounds to the midpoint.
 */
const BACKDROP_BRIGHTNESS_THRESHOLD = 0.5;

/**
 * The sampled region is inflated to at least this many CSS px per axis so a hairline rect samples
 * its surroundings instead of a couple of pixel rows.
 */
const MIN_SAMPLE_BAND_CSS_PX = 16;

/**
 * The clamped screen region is averaged through a canvas this size, keeping the cost constant no
 * matter how large the rect is on screen.
 */
const SAMPLE_GRID_WIDTH = 32;
const SAMPLE_GRID_HEIGHT = 8;

/**
 * Transparent-poster pixels carry alpha < 1 (the fabric canvas background is '#00000000'); the
 * editor shows a light workspace through them, so composite samples over white before measuring.
 */
const TRANSPARENCY_COMPOSITE_COLOR = '#ffffff';

const RGBA_CHANNELS_PER_PIXEL = 4;

/**
 * Picks black or white — whichever contrasts with the pixels currently rendered under the given
 * scene-space rect on the page's canvas. Reads the on-screen canvas element directly, so whatever
 * the user sees (zoom, the current video frame, filters) is what gets judged.
 *
 * Falls back to black whenever sampling is impossible (rect fully off-screen, no 2d context, or a
 * pixel-read error): black stays visible on the typical light poster, unlike the uniform shape gray.
 */
export const getContrastingFillForSceneRect = (page: Page, sceneRect: TBBox): RGBA => {
  try {
    const brightness = getAverageBackdropBrightness(page, sceneRect);
    if (brightness === null || brightness >= BACKDROP_BRIGHTNESS_THRESHOLD) {
      return BLACK_FILL;
    }
    return WHITE_FILL;
  } catch {
    return BLACK_FILL;
  }
};

const getAverageBackdropBrightness = (page: Page, sceneRect: TBBox): number | null => {
  const canvas = page.fabricCanvas;
  const backstoreRect = getClampedBackstoreRect(sceneRect, canvas);
  if (backstoreRect === null) {
    return null;
  }

  const sampleCanvas = document.createElement('canvas');
  sampleCanvas.width = SAMPLE_GRID_WIDTH;
  sampleCanvas.height = SAMPLE_GRID_HEIGHT;
  const context = sampleCanvas.getContext('2d');
  if (context === null) {
    return null;
  }

  context.fillStyle = TRANSPARENCY_COMPOSITE_COLOR;
  context.fillRect(0, 0, SAMPLE_GRID_WIDTH, SAMPLE_GRID_HEIGHT);
  context.drawImage(canvas.getElement(), backstoreRect.left, backstoreRect.top, backstoreRect.width, backstoreRect.height, 0, 0, SAMPLE_GRID_WIDTH, SAMPLE_GRID_HEIGHT);

  const {data} = context.getImageData(0, 0, SAMPLE_GRID_WIDTH, SAMPLE_GRID_HEIGHT);
  let brightnessSum = 0;
  for (let i = 0; i < data.length; i += RGBA_CHANNELS_PER_PIXEL) {
    brightnessSum += getPerceivedBrightness(data[i], data[i + 1], data[i + 2]);
  }
  return brightnessSum / (SAMPLE_GRID_WIDTH * SAMPLE_GRID_HEIGHT);
};

/**
 * Scene rect → backstore pixel rect of the page's rendered canvas element: viewport transform to
 * CSS px (the exact inverse of getScenePoint), inflate to the minimum sample band, retina-scale,
 * clamp to the element. Null when nothing of the rect is on screen.
 */
const getClampedBackstoreRect = (sceneRect: TBBox, canvas: Page['fabricCanvas']): TBBox | null => {
  const {viewportTransform} = canvas;
  const cornerA = new Point(sceneRect.left, sceneRect.top).transform(viewportTransform);
  const cornerB = new Point(sceneRect.left + sceneRect.width, sceneRect.top + sceneRect.height).transform(viewportTransform);

  const [left, right] = inflateSpanToMinimum(Math.min(cornerA.x, cornerB.x), Math.max(cornerA.x, cornerB.x), MIN_SAMPLE_BAND_CSS_PX);
  const [top, bottom] = inflateSpanToMinimum(Math.min(cornerA.y, cornerB.y), Math.max(cornerA.y, cornerB.y), MIN_SAMPLE_BAND_CSS_PX);

  const retinaScaling = canvas.getRetinaScaling();
  const element = canvas.getElement();
  const clampedLeft = Math.max(0, Math.floor(left * retinaScaling));
  const clampedTop = Math.max(0, Math.floor(top * retinaScaling));
  const clampedRight = Math.min(element.width, Math.ceil(right * retinaScaling));
  const clampedBottom = Math.min(element.height, Math.ceil(bottom * retinaScaling));

  if (clampedRight - clampedLeft < 1 || clampedBottom - clampedTop < 1) {
    return null;
  }
  return {
    left: clampedLeft,
    top: clampedTop,
    width: clampedRight - clampedLeft,
    height: clampedBottom - clampedTop,
  };
};

const inflateSpanToMinimum = (start: number, end: number, minimumSpan: number): [number, number] => {
  if (end - start >= minimumSpan) {
    return [start, end];
  }
  const center = (start + end) / 2;
  return [center - minimumSpan / 2, center + minimumSpan / 2];
};
